import {
  type StyleObjectNonMulti,
  type StyleFunctionProps,
} from '@frontend/components/src/theme/styleConfigHelpers';

import { TABS_PARTS } from './tabsParts';
import type { TabsColorScheme, TabsStyleConfig, TabsVariant } from './types';

type MarginValue = StyleObjectNonMulti['m'];

type TabMarginSide = 'beginning' | 'end';

const TAB_ALIGNMENTS = ['end', 'center', 'start'] as const;

type TabAlignment = (typeof TAB_ALIGNMENTS)[number];

const isTabAlignment = (value: unknown): value is TabAlignment =>
  TAB_ALIGNMENTS.includes(value as TabAlignment);

const TAB_MARGINS: Record<TabMarginSide, Record<TabAlignment, MarginValue>> = {
  beginning: {
    end: 'auto',
    center: 'auto',
    start: 0,
  },
  end: {
    end: 0,
    center: 'auto',
    start: 'auto',
  },
};

type GetTabMarginValueProps = {
  side: TabMarginSide;
  alignment: unknown;
};

const getTabMarginValue = ({
  side,
  alignment,
}: GetTabMarginValueProps): MarginValue => {
  if (isTabAlignment(alignment)) {
    return TAB_MARGINS[side][alignment];
  }

  return undefined;
};

type GetTabListPseudoElementStylesProps = {
  orientation: StyleFunctionProps<TabsVariant, TabsColorScheme>['orientation'];
  color: StyleObjectNonMulti['color'];
  variant: TabsVariant;
};

/**
 * Styling for pseudo elements that make the line separating the tabs from the content.
 * Done to:
 * - Ensure the line has a lower z-index than the selection indicator;
 * - Ensure the line is near the tabs and not offset by a scrollbar in cases of overflow.
 */
const getTabListPseudoElementStyles = ({
  orientation,
  color,
  variant,
}: GetTabListPseudoElementStylesProps): StyleObjectNonMulti => {
  const sharedStyles = {
    content: `" "`, // `` quotes solve an issue where chakra wasn't propogating content down to pseudo-element
    color: 'transparent',
    flex: 0,
    alignSelf: 'flex-end',
    bg: color,
  };

  return {
    ...(orientation === 'vertical'
      ? {
          _after: {
            ...sharedStyles,
            top: 0,
            position: 'absolute',
            bottom: 0,
            right: variant === 'enclosed' ? 0 : 'unset',
            left: variant === 'enclosed' ? 'unset' : 0,
            lineHeight: '0px',
            w: '1px',
            bg: color,
          },
        }
      : {
          _before: {
            ...sharedStyles,
            position: 'sticky',
            display: 'block',
            left: 0,
            minWidth: '100%',
            marginRight: '-100% !important',
            h: '1px',
          },
        }),
  };
};

const baseStyle: TabsStyleConfig['baseStyle'] = ({
  orientation,
  isFitted,
  align,
}) => ({
  root: {
    display: 'flex',
    ...(orientation === 'vertical'
      ? { flexDirection: 'row', maxHeight: '100%' }
      : {
          flexDirection: 'column',
        }),
    overflow: 'hidden',
  },
  tab: {
    position: 'relative',
    flex: isFitted ? 1 : undefined,
    transitionProperty: 'common',
    transitionDuration: 'normal',
    color: 'feature.secondary',
    fontWeight: 'medium',
    cursor: 'pointer',
    minWidth: 'max-content',
    zIndex: 1, // give this a z index by default so it appears on top of the line pseudo element.
    _hover: {
      '.tabs_quantitybadge': {
        bg: 'interaction.hover',
        color: 'feature.onInteraction',
      },
    },
    _selected: {
      '.tabs_quantitybadge': {
        bg: 'interaction.main',
        color: 'feature.onInteraction',
      },
      _disabled: {
        '.tabs_quantitybadge': {
          bg: 'interaction.disabled',
          color: 'feature.placeholder',
        },
      },
    },
    _disabled: {
      '.tabs_quantitybadge': {
        bg: 'interaction.surfaceDisabled',
        color: 'interaction.disabled',
      },
    },
    // Using auto left/right or top/bottom margins to ensure overflow scrolling works...
    // ...instead of using justify: flex-end, which fails
    ...(orientation === 'vertical'
      ? {
          w: '100%',
          justifyContent: 'flex-start',
          _first: {
            mt: getTabMarginValue({ side: 'beginning', alignment: align }),
          },
          _last: {
            mb: getTabMarginValue({ side: 'end', alignment: align }),
          },
        }
      : {
          justifyContent: 'center',
          _first: {
            ml: getTabMarginValue({ side: 'beginning', alignment: align }),
          },
          _last: {
            mr: getTabMarginValue({ side: 'end', alignment: align }),
          },
        }),
    _focusVisible: {
      zIndex: 2,
      boxShadow: 'focusInset',
    },
  },
  tablist: {
    justifyContent: 'flex-start',
    position: 'relative',
    ...(orientation === 'vertical'
      ? {
          flexDirection: 'column',
          alignItems: 'flex-end',
          overflowX: 'hidden',
          overflowY: 'auto',
          minWidth: 'max-content',
          maxHeight: '100%',
        }
      : {
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'hidden',
          minHeight: 'max-content',
        }),
  },
  tabpanel: {
    p: 2,
    color: 'feature.primary',
    textAlign: 'left',
    ...(orientation === 'vertical' && {
      maxHeight: '100%',
      overflowY: 'auto',
    }),
  },
  number: {
    bg: 'page.plateMedium',
    color: 'feature.secondary',
    fontWeight: 'heavy',
    borderRadius: 'sm',
    transitionDuration: 'normal',
    transitionProperty: 'common',
    textAlign: 'center',
  },
});

const SIZES: TabsStyleConfig['sizes'] = {
  sm: ({ orientation }) => ({
    tab: {
      fontSize: 'sm',
      lineHeight: 'sm',
      minHeight: 3,
      px: 1,
      _notFirst: {
        [orientation === 'vertical' ? 'mt' : 'ml']: 0.5,
      },
      gap: '0.25rem',
    },
    number: {
      fontSize: 'sm',
      lineHeight: '1rem',
      px: '0.25rem',
      minWidth: '1.125rem',
    },
  }),
  md: ({ orientation }) => ({
    tab: {
      fontSize: 'md',
      lineHeight: 'md',
      minHeight: 4,
      px: 1,
      _notFirst: {
        [orientation === 'vertical' ? 'mt' : 'ml']: 1,
      },
      gap: '0.375rem',
    },
    number: {
      fontSize: 'sm',
      lineHeight: '1.125rem',
      px: '0.3125rem',
      minWidth: '1.25rem',
    },
  }),
  lg: ({ orientation }) => ({
    tab: {
      fontSize: 'lg',
      lineHeight: 'lg',
      minHeight: 5,
      px: 1.5,
      _notFirst: {
        [orientation === 'vertical' ? 'mt' : 'ml']: 1,
      },
      gap: '0.5rem',
    },
    number: {
      fontSize: 'md',
      lineHeight: '1.25rem',
      px: '0.375rem',
      minWidth: '1.5rem',
    },
  }),
};

const VARIANTS: TabsStyleConfig['variants'] = {
  default: ({ orientation }) => ({
    tab: {
      _after: {
        content: `"-"`, // `` quotes solve an issue where chakra wasn't propogating content down to pseudo-element
        color: 'transparent',
        position: 'absolute',
        transitionProperty: 'common',
        transitionDuration: 'normal',
        ...(orientation === 'vertical'
          ? { w: '2px', bottom: 0, top: 0, left: 0 }
          : { h: '2px', right: 0, left: 0, bottom: 0 }),
      },
      _hover: {
        color: 'interaction.hover',
        _after: {
          bg: 'interaction.hover',
        },
      },
      _selected: {
        color: 'interaction.main',
        _after: {
          bg: 'interaction.main',
        },
        _disabled: {
          color: 'feature.placeholder',
        },
      },
      _disabled: {
        color: 'interaction.disabled',
        cursor: 'not-allowed',
        bg: 'transparent',
        _after: {
          bg: 'transparent',
        },
      },
    },
    tablist: {
      ...getTabListPseudoElementStyles({
        orientation,
        color: 'separator.divider',
        variant: 'default',
      }),
    },
  }),
  filled: ({ orientation }) => ({
    tab: {
      color: 'feature.primary',
      fontWeight: 'default',
      _after: {
        content: `"-"`, // `` quotes solve an issue where chakra wasn't propogating content down to pseudo-element
        color: 'transparent',
        position: 'absolute',
        transitionProperty: 'common',
        transitionDuration: 'normal',
        ...(orientation === 'vertical'
          ? { w: '2px', bottom: 0, top: 0, left: 0 }
          : { h: '2px', right: 0, left: 0, bottom: 0 }),
      },
      _hover: {
        bg: 'interaction.surfaceHover',
        _after: {
          bg: 'interaction.surfaceHover',
        },
      },
      _selected: {
        fontWeight: 'medium',
        bg: 'interaction.surfaceActive',
        _after: {
          bg: 'interaction.main',
        },
        _disabled: {
          color: 'feature.placeholder',
        },
      },
      _disabled: {
        color: 'interaction.disabled',
        cursor: 'not-allowed',
        bg: 'transparent',
        _after: {
          bg: 'transparent',
        },
      },
    },
    tablist: {
      ...getTabListPseudoElementStyles({
        orientation,
        color: 'separator.divider',
        variant: 'default',
      }),
    },
  }),
  enclosed: ({ orientation }) => ({
    tab: {
      ...(orientation === 'vertical'
        ? {
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderLeft: '1px solid',
            pr: '1px',
            borderTopLeftRadius: 'lg',
            borderBottomLeftRadius: 'lg',
          }
        : {
            borderTop: '1px solid',
            borderLeft: '1px solid',
            borderRight: '1px solid',
            pb: '1px',
            borderTopLeftRadius: 'lg',
            borderTopRightRadius: 'lg',
          }),
      borderColor: 'transparent',
      _hover: {
        color: 'interaction.hover',
      },
      _disabled: {
        color: 'interaction.disabled',
        cursor: 'not-allowed',
      },
      _selected: {
        color: 'interaction.main',
        borderColor: 'separator.border',
        _disabled: {
          color: 'feature.placeholder',
        },
        ...(orientation === 'vertical'
          ? {
              _after: {
                content: `"-"`, // `` quotes solve an issue where chakra wasn't propogating content down to pseudo-element
                color: 'transparent',
                position: 'absolute',
                w: '1px',
                bottom: 0,
                right: 0,
                top: 0,
                bg: 'page.surface',
                transitionProperty: 'common',
                transitionDuration: 'normal',
              },
            }
          : {
              _after: {
                content: `"-"`, // `` quotes solve an issue where chakra wasn't propogating content down to pseudo-element
                color: 'transparent',
                position: 'absolute',
                h: '1px',
                width: '100%',
                bottom: 0,
                right: 0,
                left: 0,
                bg: 'page.surface',
                transitionProperty: 'common',
                transitionDuration: 'normal',
              },
            }),
        // Using pseudoelement instead of bottom border so there aren't chamfers at border corners
      },
    },
    tablist: {
      ...getTabListPseudoElementStyles({
        orientation,
        color: 'separator.border',
        variant: 'enclosed',
      }),
    },
  }),
  unstyled: {},
};

const DEFAULT_PROPS = {
  size: 'lg',
  variant: 'default',
} as const;

export const TABS_STYLE_CONFIG: TabsStyleConfig = {
  parts: TABS_PARTS,
  baseStyle,
  sizes: SIZES,
  variants: VARIANTS,
  defaultProps: DEFAULT_PROPS,
};
